import React, { useMemo } from 'react';
import {
  Box,
  Button,
  ButtonSpinner,
  ButtonText,
  Heading,
  Icon,
  Spinner,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react-native';

import ContentGroup from '@/common/components/ContentGroup';
import { PersonRow, peerInitials } from '@/common/components/people/PersonRow';

import { useListConnections } from '@/common/api/endpoints/connections/connections';
import type { ConnectionView } from '@/common/api/models/connectionView';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useConnectionActions } from './hooks/useConnectionActions';

function peerReach(connection: ConnectionView) {
  return {
    email: connection.peer.email ?? null,
    phone: connection.peer.phone ?? null,
    whatsapp: connection.peer.whatsapp ?? null,
  };
}

type ConnectionsSectionProps = {
  testID: string;
  label: string;
  count: number;
  children: React.ReactNode;
};

function ConnectionsSection({
  testID, label, count, children,
}: ConnectionsSectionProps) {
  if (count === 0) return null;

  return (
    <VStack space="sm" testID={testID}>
      <Text size="xs" color="$textHint" fontWeight="bold" textTransform="uppercase">
        {label}
        {' '}
        (
        {count}
        )
      </Text>
      {children}
    </VStack>
  );
}

/**
 * My connections — pending in/out sections and accepted edges with reach links.
 */
export default function MyConnectionsScreen() {
  const { t } = useTranslation();
  const { isFeatureActive } = useUserFeatures();
  const connectionsEnabled = isFeatureActive('student-connections');
  const actions = useConnectionActions();

  const connectionsQuery = useListConnections({
    query: { enabled: connectionsEnabled },
  });

  const grouped = useMemo(() => {
    const list = connectionsQuery.data?.data?.data ?? [];
    return {
      pendingIn: list.filter((c) => c.direction === 'pending_in'),
      pendingOut: list.filter((c) => c.direction === 'pending_out'),
      accepted: list.filter((c) => c.direction === 'accepted'),
    };
  }, [connectionsQuery.data]);

  const isEmpty = !connectionsQuery.isLoading
    && !connectionsQuery.isError
    && grouped.pendingIn.length === 0
    && grouped.pendingOut.length === 0
    && grouped.accepted.length === 0;

  const renderRow = (connection: ConnectionView) => {
    const rowTestId = `person-row-${connection.id}`;
    const displayState = actions.getDisplayState(connection);
    const firstName = connection.peer.firstName ?? '';
    return (
      <PersonRow
        key={connection.id}
        firstName={firstName}
        initials={peerInitials(firstName)}
        photoUrl={connection.peer.profilePhotoUrl ?? null}
        reach={peerReach(connection)}
        state={displayState}
        errorMessage={actions.getRowError(connection.id)}
        rowTestId={rowTestId}
        isActionPending={actions.isRowPending(connection.id)}
        onAccept={() => actions.accept(connection.id)}
        onDecline={() => actions.decline(connection.id)}
        onWithdraw={() => actions.withdraw(connection.id)}
        onRemove={() => actions.confirmRemove(connection)}
        onRetry={() => actions.retryRow(connection.id)}
      />
    );
  };

  if (!connectionsEnabled) {
    return null;
  }

  if (connectionsQuery.isLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" testID="my-connections-loading">
        <Spinner size="large" />
      </Box>
    );
  }

  return (
    <Box
      flex={1}
      bg="$bgApp"
      px="$4"
      py="$6"
      testID="my-connections-screen"
    >
      <VStack space="2xl" maxWidth={800} alignSelf="center" width="100%">
        <Heading size="lg">{t('people.connections.title')}</Heading>

        {!!connectionsQuery.isError && (
          <ContentGroup variant="subtle" bg="$statusErrorSubtle" testID="connections-load-error">
            <VStack space="md">
              <Text size="md" color="$textOnErrorSubtle" fontWeight="medium">
                {t('people.connections.loadErrorTitle')}
              </Text>
              <Text size="sm" color="$textOnErrorSubtle">
                {t('people.connections.loadErrorBody')}
              </Text>
              <Button
                variant="outline"
                testID="connections-load-try-again"
                onPress={() => connectionsQuery.refetch()}
                isDisabled={connectionsQuery.isFetching}
              >
                {connectionsQuery.isFetching ? <ButtonSpinner /> : null}
                <ButtonText>{t('people.connections.tryAgain')}</ButtonText>
              </Button>
            </VStack>
          </ContentGroup>
        )}

        {!!isEmpty && (
          <VStack space="md" alignItems="center" py="$8" testID="connections-empty">
            <Icon as={Users} size="xl" color="$textHint" />
            <Text size="lg" color="$textSubtle" textAlign="center">
              {t('people.connections.emptyTitle')}
            </Text>
            <Text size="sm" color="$textHint" textAlign="center">
              {t('people.connections.emptyBody')}
            </Text>
          </VStack>
        )}

        {!isEmpty && !connectionsQuery.isError && (
          <VStack space="2xl">
            <ConnectionsSection
              testID="connections-section-pending-in"
              label={t('people.connections.pendingIn')}
              count={grouped.pendingIn.length}
            >
              {grouped.pendingIn.map(renderRow)}
            </ConnectionsSection>

            <ConnectionsSection
              testID="connections-section-pending-out"
              label={t('people.connections.pendingOut')}
              count={grouped.pendingOut.length}
            >
              {grouped.pendingOut.map(renderRow)}
            </ConnectionsSection>

            <ConnectionsSection
              testID="connections-section-accepted"
              label={t('people.connections.accepted')}
              count={grouped.accepted.length}
            >
              {grouped.accepted.map(renderRow)}
            </ConnectionsSection>
          </VStack>
        )}
      </VStack>
    </Box>
  );
}
